/* global $ */
// Possible user types
export const userTypes = {
  coloc: 1,
  proprio: 2,
  agent: 4,
  manager: 8,

  /**
	 * Return true if user is one of those types
	 * @param {User}  user
	 * @param {String|int}  type    coloc, proprio, agent, or directly 1,2,4
	 */
  is(user, type) {
    // Type convertion
    if (typeof type === 'string') {
      type = this[type]
    }
    return user && (user.type & type) === type
  },

  getType(user) {
    if (user.type === 2) return 'proprio'
    if (user.type === 1 && user.home && !user.home.is_coloc) return 'proprio'
    if (user.type === 1) return 'coloc'
    if (user.type === 4) return 'agent'
    if (user.type === 8) return 'manager'
    return 'unknown'
  },

  /**
	 * Return the base url for the user by type
	 * @param {User}  user
	 * @param {String|int}  type    coloc, proprio, agent, or directly 1,2,4
	 */
  baseURL(user) {
    if ((user.type & this.coloc) !== 0) return 'users'
    if ((user.type & this.proprio) !== 0) return 'proprietaire'
    if ((user.type & this.agent) !== 0) return 'agent'
    if ((user.type & this.manager) !== 0) return 'manager'
    return 'users'
  },
}

/**
 * Possible routes ordered for the current user
 * @param {User?} user    Current user or empty user to get all possible routes
 */
export const routeOrder = (user) => {
  const res = []
  const isColoc = user && userTypes.is(user, 'coloc')

  if (!user || isColoc) {
    res.push(
      'lifestyle-1',
      'lifestyle-2',
      'lifestyle-3',
      'lifestyle-4',
      'lifestyle-5',
      'lifestyle-6',
      'personality-1',
      'personality-2',
      'athome-1',
      'athome-2',
      'employment',
      'personality-3',
      'personality-4',
      'personality-5',
      'home-1',
    )
    if (!user || user.home) {
      // Tenants having a home (not pure owners)
      res.push(
        'home-1b',
        'home-2',
        'home-3',
      )

      if (user.home && !user.home.is_coloc) {
        res.push(
          'home-3b',
          'home-3c',
          'offres',
          'annonce-moderation',
          'annonce-paiement',
          'succes-paiement',
        )
      } else {
        res.push(
          'home-4',
        )
      }
    }
    res.push('search')
  }
  if (!user || !isColoc) {
    res.push(
      'annonce-adresse',
      'annonce-description',
      'annonce-chambres',
      'annonce-charges',
      'annonce-photo-video',
      'annonce-recapitulatif',
      'offres',
      'annonce-moderation',
      'annonce-paiement',
      'succes-paiement',
    )
  }
  return res
}

/**
 * Convert the url path to filename
 * @param {string} path Path in url
 * @returns {string}
 */
export const pathToFilename = (path) => {
  // Pro url to component
  switch (path) {
  case 'annonce-adresse':
  case 'home-2':
    return 'home/step1'
  case 'annonce-description':
  case 'home-3':
    return 'home/step2'
  case 'annonce-chambres':
  case 'home-3b':
    return 'home/step2b'
  case 'annonce-charges':
  case 'home-3c':
    return 'home/step2c'
  case 'annonce-photo-video':
  case 'home-4':
    return 'home/step3'
  case 'annonce-recapitulatif':
    return 'Checkout'
  case 'offres':
    return 'ProOffers'
  case 'annonce-moderation':
    return 'ProModeration'
  case 'annonce-paiement':
    return 'Checkout'
  case 'succes-paiement':
    return 'PaymentSuccess'
  }

  // Default to Upper-case-first
  return path.charAt(0).toUpperCase() + path.slice(1)
}

/**
 * Save the model and go to prev/next step
 * @param {Vue} vue Vue instance
 * @param {string} model "user, profile, home, search, ignore" to update
 * @param {int} direction forward 1, backward -1
 */
export const goToPage = (vue, model, direction) => {
  let url, data
  let method = 'put'
  let next = null
  let isUser = false

  switch (model) {
  case 'ignore':
    data = {} // Only registration step
    url = `/api/users/${vue.user.id}`
    isUser = true
    break

  case 'user':
    data = { ...vue.user }
    delete data.home
    delete data.profile
    delete data.search
    delete data.company
    url = `/api/users/${data.id}`
    isUser = true
    break

  case 'profile':
    data = { ...vue.user.profile }
    url = `/api/profiles/${data.id}`
    break

  case 'home':
    data = { ...(vue.homePro || vue.user.home) }
    url = `/api/homes/${data.id}`
    break
  case 'payment':
    next = 'annonce-paiement'
    data = { ...(vue.homePro || vue.user.home) }
    url = `/api/homes/${data.id}`
    break
  case 'moderation':
    next = 'annonce-moderation'
    data = { ...(vue.homePro || vue.user.home) }
    url = `/api/homes/${data.id}`
    break

  case 'search':
    data = { ...vue.user.search }
    if (data && data.id) {
      url = `/api/searches/${data.id}`
    } else {
      method = 'post'
      url = '/api/searches'
    }
    break

  default:
    return console.error('tried to save model: ', model)
  }

  let redirectAfterLogin = false
  if (vue.user.registration_step >= 12 && direction === 1) {
    const spageAfterLogin = sessionStorage.getItem('gotoPageAfterLogin')
    if (spageAfterLogin) {
      const pageAfterLogin = JSON.parse(spageAfterLogin)
      if (pageAfterLogin && pageAfterLogin.mode === 'request-warranty') {
        model = 'search'
        method = 'post'
        const param = pageAfterLogin.room_id ? `&room_id=${pageAfterLogin.room_id}` : ''
        url = `/api/users/${vue.user.id}/request-warranty?home_id=${pageAfterLogin.home_id}${param}`
        next = pageAfterLogin.path
        redirectAfterLogin = true
      }
    }
  }

  const routeNames = routeOrder(vue.user)
  const current = routeNames.indexOf(vue.$route.params.path)

  // Next path to navigate to
  if (!next) {
    next = direction > 0 ? routeNames[current + 1] : routeNames[current - 1]
  }

  // Store registration step
  if (!vue.homePro) {
    if (direction > 0) {
      if (!next || !next.startsWith('$')) { // Don't consider BI as a registration step
        vue.user.registration_step = current + 1
        if (isUser) { data.registration_step = current + 1 }
      }
    } else {
      vue.user.registration_step = current - 1
      if (isUser) { data.registration_step = current - 1 }
    }
  }

  if (!next) {
    next = 'DashboardUserMatching'
    if (direction > 0 && !vue.homePro) {
      vue.user.registration_step = 100
      if (isUser) { data.registration_step = 100 }
    }
  }

  // Send update
  return vue.$http[method](url, data)
    .then(res => {
      // Fix search_id on user
      if (vue.user && vue.user.search) {
        vue.user.search.budget_user_id = res.data.budget_user_id
        vue.user.search.budget_connection_url = res.data.budget_connection_url
      }
      if (model === 'search' && method === 'post') {
        if (vue.user && !vue.user.search) {
          vue.user.search = res.data
        }
        vue.user.search_id = res.data.id
        vue.user.search.warranty_link = res.data.warranty_link
        if (vue.user.search) { vue.user.search.id = res.data.id }
      }
      if (next && vue.user.search && vue.user.search.warranty_link && next.includes('$WARRANTY_LINK')) {
        next = next.replaceAll('$WARRANTY_LINK', vue.user.search.warranty_link)
      }
      if (redirectAfterLogin) {
        sessionStorage.removeItem('gotoPageAfterLogin')
      }
      if (model === 'home') {
        if (vue.homePro) {
          vue.homePro.rooms = res.data.rooms
          vue.homePro.reference_rent_auto = res.data.reference_rent_auto
          vue.homePro.reference_rent_month = res.data.reference_rent_month
          vue.homePro.inc_reference_rent_month = res.data.inc_reference_rent_month
          vue.homePro.dec_reference_rent_month = res.data.dec_reference_rent_month
        } else {
          vue.user.home.rooms = res.data.rooms
          vue.user.home.reference_rent_auto = res.data.reference_rent_auto
          vue.user.home.reference_rent_month = res.data.reference_rent_month
          vue.user.home.inc_reference_rent_month = res.data.inc_reference_rent_month
          vue.user.home.dec_reference_rent_month = res.data.dec_reference_rent_month
        }
      }

      // Analytics event
      try {
        if (window.gtag) {
          gtag('event', 'register', { action: direction > 0 ? 'next' : 'previous', step: vue.user.registration_step })
          if (next === 'DashboardMatching' || next === 'DashboardUserMatching' || next === 'DashboardHomeMatching') {
            gtag('event', 'sign_up', { method: 'Cooloc', group_id: userTypes.getType(vue.user) })
          }
        }
      } catch (err) {
        console.error(err)
      }

      // Return new url
      return next
    })
}

/**
 * Check the user registration completion, and redirect to last unfinished step if not done
 * @param {User} user
 * @returns {boolean}   Forced redirected
 */
export const checkComplete = (vue, user) => {
  const routeNames = routeOrder(user)

  if (userTypes.is(user, 'coloc') && !(user.home && !user.home.is_coloc)) {
    // Incomplete
    if (user.registration_step <= routeNames.length - 1) {
      // Step exists, and we don't want to access a previous our current step
      const index = user.registration_step
      const path = vue.$route.params.path
      const current = routeNames.indexOf(path)
      if (routeNames[index] && (current < 0 || current > index || (vue.$route && vue.$route.name !== 'registration'))) {
        vue.$router.push({ path: '/registration/' + routeNames[index].toLowerCase(), query: { forced: 1 } })
        return true
      }
    }
  }

  return false
}

/**
 * Set title based on the user and url
 * @param {Vue} vue Vue instance
 */
export const setTitle = (vue) => {
  const type = vue.$route && vue.$route.name

  if (type === 'proprietaire' || type === 'agent') {
    let title = 'COOLOC | '
    title += type === 'agent' ? 'Agence' : (type === 'manager' ? 'Gestionnaire' : 'Propriétaire')

    title += {
      'annonce-adresse': ' - Annonce Adresse',
      'annonce-description': ' - Annonce Description',
      'annonce-chambres': ' - Annonce Chambres',
      'annonce-charges': ' - Annonce Charges',
      'annonce-photo-video': ' - Annonce Photo',
      'annonce-recapitulatif': ' - Annonce Récapitulatif',
      offres: ' - Offres',
      'annonce-paiement': ' - Annonce Paiement',
      'succes-paiement': ' - Paiement OK',
    }[vue.$route.params.path] || ''

    document.title = title
  } else if (vue.$route.params.path === 'search') {
    document.title = 'COOLOC | Profil matching'
  } else {
    document.title = 'COOLOC | Profil page ' + (routeOrder(vue.user).indexOf(vue.$route.params.path) + 1)
  }
  setTimeout(() => $(window).trigger('title-change'), 100)
}
